import { supabase } from './supabase.js';

/**
 * İstatistikler.
 *
 * Online maçları SUNUCU yazıyor (servis anahtarıyla, doğrulanmış). Burada
 * yazılan yalnızca BOTLARA KARŞI oynanan maçlar: onlar tamamen tarayıcıda
 * geçtiği için sunucunun haberi yok. Bu yüzden `mode: 'bot'` ile ayrı
 * işaretleniyor ve istatistiklerde ayrı gösteriliyor — istemcinin bildirdiği
 * rakam, sunucunun doğruladığı rakamla aynı sütunda toplanmamalı.
 *
 * Veritabanı da bunu zorluyor: satır güvenliği kuralları oyuncunun yalnızca
 * `mode = 'bot'` maçına ve yalnızca KENDİ sonucuna yazmasına izin veriyor.
 */

export interface BotMatchResult {
  seed: string;
  deals: number;
  /** Oyuncunun kendi sonucu — bot koltukları yazılmıyor, kimlikleri yok. */
  seat: number;
  score: number;
  place: number;
}

export async function saveBotMatch(result: BotMatchResult): Promise<void> {
  if (!supabase) return;

  const { data: session } = await supabase.auth.getSession();
  const userId = session.session?.user.id;
  if (!userId) return; // hesapsız oynanıyor, kaydedilecek bir yer yok

  try {
    const { data: match, error: matchError } = await supabase
      .from('matches')
      .insert({
        seed: result.seed,
        deals: result.deals,
        mode: 'bot',
        finished_at: new Date().toISOString(),
      })
      .select('id')
      .single();
    if (matchError || !match) throw matchError ?? new Error('maç satırı dönmedi');

    const { error: playerError } = await supabase.from('match_players').insert({
      match_id: match.id,
      seat: result.seat,
      user_id: userId,
      score: result.score,
      place: result.place,
      bot_moves: 0,
    });
    if (playerError) throw playerError;
  } catch (error) {
    // Kayıt tutulamaması oyunu bozmamalı: maç bitti, sonuç ekranda.
    console.error('Bot maçı kaydedilemedi:', error);
  }
}

/** Sıralama dönemi — oyunlardaki gibi günlük, haftalık ve genel. */
export type Period = 'day' | 'week' | 'all';
export type Mode = 'online' | 'bot';

export interface LeaderboardRow {
  userId: string;
  username: string;
  displayName: string;
  matches: number;
  wins: number;
  lastPlaces: number;
  totalPoints: number;
  avgPlace: number | null;
}

/**
 * Sıralama tablosu.
 *
 * Görünüm yerine fonksiyon çağrılıyor: satır güvenliği kuralı oyuncuya
 * yalnızca kendi maçlarını gösterdiği için doğrudan sorguda herkesin sayısı
 * sıfır çıkıyordu. Fonksiyon tanımlayıcı yetkisiyle çalışıp yalnızca
 * toplamları veriyor.
 */
export async function fetchLeaderboard(
  period: Period = 'all',
  mode: Mode = 'online',
): Promise<LeaderboardRow[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('leaderboard', {
    p_period: period,
    p_mode: mode,
  });

  if (error || !data) {
    console.error('Sıralama okunamadı:', error);
    return [];
  }

  return (data as Record<string, unknown>[]).map((row) => ({
    userId: String(row.user_id),
    username: String(row.username),
    displayName: String(row.display_name),
    matches: Number(row.matches ?? 0),
    wins: Number(row.wins ?? 0),
    lastPlaces: Number(row.last_places ?? 0),
    totalPoints: Number(row.total_points ?? 0),
    avgPlace: row.avg_place === null ? null : Number(row.avg_place),
  }));
}

/** Oyuncunun kendi dökümü — mod başına bir satır. */
export interface MyStats {
  mode: Mode;
  matches: number;
  wins: number;
  seconds: number;
  thirds: number;
  lastPlaces: number;
  totalPoints: number;
  avgPlace: number | null;
}

export async function fetchMyStats(): Promise<MyStats[]> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('my_stats');
  if (error || !data) {
    console.error('Kişisel istatistik okunamadı:', error);
    return [];
  }
  return (data as Record<string, unknown>[]).map((row) => ({
    mode: row.mode === 'bot' ? 'bot' : 'online',
    matches: Number(row.matches ?? 0),
    wins: Number(row.wins ?? 0),
    seconds: Number(row.seconds ?? 0),
    thirds: Number(row.thirds ?? 0),
    lastPlaces: Number(row.last_places ?? 0),
    totalPoints: Number(row.total_points ?? 0),
    avgPlace: row.avg_place === null ? null : Number(row.avg_place),
  }));
}
